/**
 * Edge geometry + styling for the OBSIDIAN React/SVG renderer.
 *
 * Faithful port of obgraph.js `layoutEdgesAndNodes` / `build` edge logic and
 * graph-spec.md §2. Pure functions returning plain data/strings — no DOM access
 * — so this module stays safe for the server-consumed graph-logic build.
 *
 *  - quadratic-bow path: control point offset perpendicular to the midpoint by
 *    `len * 0.13 * 0.5`.
 *  - color: critical → red (#dc2626); annotation → muted; else source category EDGE color.
 *  - widths 1.1 / 1.4 / 1.8 ; opacities 0.42 / 0.7 / 0.95 (rest / critical / active).
 *  - property (annotation) edges: dotted "1 4", arrowless, muted.
 *  - select-only flow overlay: dasharray "2 9", @keyframes obg-flow.
 */

import { getEdgeColor } from '../categoryColors';
import { categoryColors } from './ontologyGlyph';

export type EdgeKind = 'relationship' | 'activity' | 'annotation';

/** Critical-red used for flagged edges and arrowheads (graph-spec.md §2/§4). */
export const CRITICAL_EDGE_COLOR = '#dc2626';

/** Muted property-edge color. Brighter in dark (--color-text-secondary) so the dotted
 *  leaders stay legible on the deep --graph-canvas; quieter (--color-text-muted) in light. */
export const PROPERTY_EDGE_COLOR_VAR = 'var(--color-text-muted)';
export const PROPERTY_EDGE_COLOR_DARK = 'var(--color-text-secondary)';
export function propertyEdgeColor(dark: boolean): string {
  return dark ? PROPERTY_EDGE_COLOR_DARK : PROPERTY_EDGE_COLOR_VAR;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Classify an edge by its endpoints' node types (obgraph.js `edgeKind` /
 * graph-spec.md §2 `gtxEdgeClass`). geo/mitre endpoint → annotation (property
 * link); alert endpoint → activity; otherwise relationship.
 */
export function edgeKind(sourceType: string | undefined, targetType: string | undefined): EdgeKind {
  const isProp = (t?: string) => t === 'geolocation' || t === 'mitre_technique' || t === 'geo' || t === 'mitre';
  if (isProp(sourceType) || isProp(targetType)) return 'annotation';
  if (sourceType === 'alert' || targetType === 'alert' || sourceType === 'incident' || targetType === 'incident') {
    return 'activity';
  }
  return 'relationship';
}

/**
 * Resolve the stroke color for an edge:
 *  - annotation (property) edge → muted var
 *  - critical → red
 *  - else → source node's category edge color
 */
export function resolveEdgeColor(opts: {
  kind: EdgeKind;
  critical: boolean;
  sourceType: string | undefined;
  dark: boolean;
}): string {
  if (opts.kind === 'annotation') return propertyEdgeColor(opts.dark);
  if (opts.critical) return CRITICAL_EDGE_COLOR;
  return getEdgeColor(opts.sourceType ?? 'unknown', opts.dark);
}

/** Quadratic-bow control point: perpendicular to the midpoint by len*0.13*0.5. */
export function bowControlPoint(a: Point, b: Point): Point {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const bo = len * 0.13 * 0.5;
  return { x: mx - (dy / len) * bo, y: my + (dx / len) * bo };
}

/** Full quadratic path string `M..Q..` for the bowed edge. */
export function bowPath(a: Point, b: Point): { d: string; control: Point } {
  const c = bowControlPoint(a, b);
  return { d: `M${a.x},${a.y} Q${c.x},${c.y} ${b.x},${b.y}`, control: c };
}

/** Stroke width by state (graph-spec.md §2). */
export function edgeWidth(opts: { kind: EdgeKind; critical: boolean; active: boolean }): number {
  if (opts.kind === 'annotation') return 1;
  if (opts.active) return 1.8;
  if (opts.critical) return 1.4;
  return 1.1;
}

/** Stroke opacity by state (graph-spec.md §2). */
export function edgeOpacity(opts: { kind: EdgeKind; critical: boolean; active: boolean }): number {
  if (opts.kind === 'annotation') return 0.6;
  if (opts.active) return 0.95;
  if (opts.critical) return 0.7;
  return 0.42;
}

/** Dotted dash pattern for property/annotation edges. */
export const ANNOTATION_DASH = '1 4';
/** Dash pattern for the select-only flow overlay. */
export const FLOW_DASH = '2 9';
/** @keyframes used by the flow overlay (matches obgraph.js `obg-flow`). */
export const FLOW_KEYFRAMES = '@keyframes obg-flow{to{stroke-dashoffset:-22}}';

export interface ArrowMarker {
  id: string;
  /** color the arrowhead fill resolves to (a hex, since category colors are hex). */
  color: string;
  markerWidth: number;
  markerHeight: number;
  refX: number;
  refY: number;
  /** the triangle polygon `points` */
  path: string;
}

/**
 * Per-category + critical arrowhead marker defs (shared across all edges),
 * matching obgraph.js `obg-ar-<cat>` / `obg-ar-crit`. Categories are the eight
 * in CATEGORY_COLORS; markers are keyed `obg-ar-<category>` and `obg-ar-crit`.
 */
export function buildArrowMarkers(dark: boolean): ArrowMarker[] {
  const categories = ['identity', 'compute', 'network', 'data', 'findings', 'application', 'context', 'framework'];
  const markers: ArrowMarker[] = categories.map((cat) => ({
    id: `obg-ar-${cat}`,
    color: categoryColors(catSampleType(cat), dark).edge,
    markerWidth: 7,
    markerHeight: 7,
    refX: 6,
    refY: 3.5,
    path: 'M0,0 L7,3.5 L0,7 z',
  }));
  markers.push({
    id: 'obg-ar-crit',
    color: CRITICAL_EDGE_COLOR,
    markerWidth: 8,
    markerHeight: 8,
    refX: 6.5,
    refY: 4,
    path: 'M0,0 L8,4 L0,8 z',
  });
  return markers;
}

/** A representative type for each category, so categoryColors() resolves its edge hue. */
function catSampleType(cat: string): string {
  switch (cat) {
    case 'identity': return 'user';
    case 'compute': return 'host';
    case 'network': return 'ip';
    case 'data': return 's3_bucket';
    case 'findings': return 'alert';
    case 'application': return 'application';
    case 'context': return 'geolocation';
    default: return 'policy';
  }
}

/** Marker id for an edge given its source category / critical flag. */
export function arrowMarkerId(opts: { critical: boolean; sourceCategory: string }): string {
  return opts.critical ? 'obg-ar-crit' : `obg-ar-${opts.sourceCategory}`;
}
